import { useCallback, useEffect, useState } from 'react';

export default function useElementIsEllipsis<E extends HTMLElement>() {
  const [element, setElement] = useState<E | null>(null);
  const [innerText, setInnerText] = useState<string>('');
  const [isEllipsis, setIsEllipsis] = useState(false);

  const handleRef = useCallback((newcomer: E | null) => {
    setElement(newcomer);
    setInnerText(newcomer?.innerText ?? '');
  }, []);

  useEffect(() => {
    if (!element) {
      setIsEllipsis(false);
      return;
    }

    const observer = new ResizeObserver(entries => {
      const isEllipsis = entries.some(entry => checkEllipsis(entry.target));
      setIsEllipsis(isEllipsis);
    });

    observer.observe(element);

    return () => observer.disconnect();
  }, [element, innerText]);

  return {
    ref: handleRef,
    isEllipsis,
  };
}

export const checkEllipsis = (element: Element) => {
  // (1)  컨텐츠가 더 큰 경우
  if (element.scrollWidth > element.clientWidth) return true;
  // (2) 자식 엘리먼트가 부모보다 작거나 같으면, 툴팁이 없거나 자체 제공하는 경우
  if (element.firstElementChild && element.firstElementChild.clientWidth <= element.clientWidth) {
    return false;
  }

  const floorToHundredths = (value: number) => Math.floor(value * 100) / 100;

  // (3) Range API를 사용하여 실제 컨텐츠 너비 측정 (소수점 단위 이슈 대응)
  // 브라우저 줌 레벨에 따른 서브픽셀 반올림 오차를 허용하기 위해 소수점 2자리 버림 비교
  const rangeWidth = floorToHundredths(getElementRangeWidth(element));
  const elementWidth = floorToHundredths(element.getBoundingClientRect().width);

  return rangeWidth > elementWidth;
};

const getElementRangeWidth = (element: Element) => {
  const range = document.createRange();
  range.selectNodeContents(element);
  return range.getBoundingClientRect().width;
};
